import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = [
    { id: "overview", label: "Overview", icon: "fa-solid fa-gauge" },
    { id: "assignments", label: "Assignments", icon: "fa-solid fa-briefcase" },
    { id: "clients", label: "Clients", icon: "fa-solid fa-users" },
    { id: "partners", label: "Partners", icon: "fa-solid fa-user-group" },
    { id: "inbox", label: "Inbox", icon: "fa-solid fa-inbox" },
    { id: "consents", label: "Consent Ledger", icon: "fa-solid fa-signature" },
    { id: "archive", label: "Archive Upload", icon: "fa-solid fa-images" },
    { id: "packages", label: "Packages", icon: "fa-solid fa-box-archive" },
    { id: "invoices", label: "Invoices", icon: "fa-solid fa-file-invoice-dollar" },
    { id: "ai", label: "AI Field Assist", icon: "fa-solid fa-wand-magic-sparkles" },
];

const STATUSES = ["new", "in_progress", "delivered", "invoiced", "paid"];

export default function Dashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("overview");

    const [assignments, setAssignments] = useState([]);
    const [clients, setClients] = useState([]);
    const [partners, setPartners] = useState([]);
    const [consents, setConsents] = useState([]);
    const [shootReqs, setShootReqs] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [packages, setPackages] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [archive, setArchive] = useState([]);

    const refresh = async () => {
        try {
            const [a, c, pr, co, sr, inq, sub, pk, iv, ar] = await Promise.all([
                api.get("/assignments"), api.get("/clients"), api.get("/partners"), api.get("/consents"),
                api.get("/shoot-requests"), api.get("/inquiries"), api.get("/subscriptions"),
                api.get("/packages"), api.get("/invoices"), api.get("/archive"),
            ]);
            setAssignments(a.data); setClients(c.data); setPartners(pr.data); setConsents(co.data);
            setShootReqs(sr.data); setInquiries(inq.data); setSubscriptions(sub.data);
            setPackages(pk.data); setInvoices(iv.data); setArchive(ar.data);
        } catch (e) { toast.error("Failed to load dashboard data"); }
    };

    useEffect(() => { refresh(); }, []);
    useEffect(() => { if (tab === "invoices" || tab === "packages" || tab === "assignments") { refresh(); } /* eslint-disable-next-line */ }, [tab]);

    const stats = [
        { k: assignments.filter((a) => a.status !== "paid").length, v: "Active assignments", c: "var(--gt-accent)" },
        { k: shootReqs.filter((r) => r.status === "new").length, v: "New shoot requests", c: "var(--gt-warn)" },
        { k: invoices.filter((i) => i.status !== "paid").length, v: "Unpaid invoices", c: "var(--gt-red)" },
        { k: packages.length, v: "Packages shipped", c: "var(--gt-ok)" },
    ];

    return (
        <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}>
            {/* Top bar */}
            <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl" style={{ background: "rgba(10,11,14,0.9)", borderBottom: "1px solid var(--gt-border)" }}>
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-3">
                        <span className="w-8 h-8 flex items-center justify-center" style={{ background: "var(--gt-accent)" }}>
                            <i className="fa-solid fa-camera-retro" style={{ color: "#1a0f0a", fontSize: 13 }}></i>
                        </span>
                        <div>
                            <div className="font-display text-lg leading-tight">First Landing Studio</div>
                            <div className="overline" style={{ fontSize: 9 }}>Owner Dashboard</div>
                        </div>
                    </a>
                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-2 text-sm">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--gt-overlay)", border: "1px solid var(--gt-border)" }}>
                                <i className="fa-solid fa-user" style={{ fontSize: 11, color: "var(--gt-text-2)" }}></i>
                            </div>
                            <span style={{ color: "var(--gt-text-2)" }}>{user?.email}</span>
                        </div>
                        <button onClick={() => nav("/")} className="gt-btn-ghost px-3 py-2 text-xs" data-testid="dash-public-btn">Public site</button>
                        <button onClick={() => { logout(); nav("/"); }} className="gt-btn-ghost px-3 py-2 text-xs" data-testid="dash-logout-btn">
                            <i className="fa-solid fa-arrow-right-from-bracket mr-1" style={{ fontSize: 10 }}></i> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="pt-16 flex">
                {/* Sidebar */}
                <aside className="hidden md:block fixed top-16 bottom-0 w-56 overflow-y-auto" style={{ borderRight: "1px solid var(--gt-border)" }}>
                    <nav className="p-3 space-y-1">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`dash-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors"
                                style={{
                                    background: tab === t.id ? "var(--gt-overlay)" : "transparent",
                                    color: tab === t.id ? "var(--gt-text)" : "var(--gt-text-2)",
                                    borderLeft: `2px solid ${tab === t.id ? "var(--gt-accent)" : "transparent"}`,
                                }}
                            >
                                <i className={t.icon} style={{ fontSize: 13 }}></i> {t.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <main className="w-full md:ml-56 p-4 sm:p-6 lg:p-8 max-w-[1300px]">
                    {/* Mobile tabs */}
                    <div className="md:hidden mb-6 overflow-x-auto flex gap-2 pb-2">
                        {TABS.map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} className="px-3 py-2 text-xs whitespace-nowrap"
                                style={{ background: tab === t.id ? "var(--gt-overlay)" : "transparent", border: "1px solid var(--gt-border)", color: tab === t.id ? "var(--gt-text)" : "var(--gt-text-2)" }}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "overview" && (
                        <section>
                            <div className="overline mb-2">Operations summary</div>
                            <h1 className="font-display text-3xl mb-8">Field desk · {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</h1>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                                {stats.map((s, i) => (
                                    <div key={i} className="gt-card p-5" data-testid={`stat-${i}`}>
                                        <div className="font-display text-3xl" style={{ color: s.c }}>{s.k}</div>
                                        <div className="overline mt-1" style={{ fontSize: 10 }}>{s.v}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                <div className="gt-card p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="overline">Recent assignments</div>
                                        <button onClick={() => setTab("assignments")} className="gt-link text-xs">View all →</button>
                                    </div>
                                    {assignments.slice(0, 5).map((a) => (
                                        <div key={a.id} className="py-3 flex items-center justify-between" style={{ borderTop: "1px solid var(--gt-border)" }}>
                                            <div>
                                                <div className="text-sm">{a.title}</div>
                                                <div className="overline" style={{ fontSize: 10 }}>{a.client?.organization || "—"} · {a.deadline || "no deadline"}</div>
                                            </div>
                                            <StatusBadge status={a.status} />
                                        </div>
                                    ))}
                                    {assignments.length === 0 && <div className="text-sm" style={{ color: "var(--gt-text-2)" }}>No assignments yet.</div>}
                                </div>
                                <div className="gt-card p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="overline">New shoot requests</div>
                                        <button onClick={() => setTab("inbox")} className="gt-link text-xs">View all →</button>
                                    </div>
                                    {shootReqs.slice(0, 5).map((r) => (
                                        <div key={r.id} className="py-3" style={{ borderTop: "1px solid var(--gt-border)" }}>
                                            <div className="text-sm">{r.organization_name}</div>
                                            <div className="overline" style={{ fontSize: 10 }}>{r.location_region} · ${r.budget_usd || "—"}</div>
                                            <p className="text-xs mt-1" style={{ color: "var(--gt-text-2)" }}>{r.brief_description?.slice(0, 110)}…</p>
                                        </div>
                                    ))}
                                    {shootReqs.length === 0 && <div className="text-sm" style={{ color: "var(--gt-text-2)" }}>Inbox empty.</div>}
                                </div>
                            </div>
                        </section>
                    )}

                    {tab === "assignments" && <AssignmentsTab assignments={assignments} clients={clients} partners={partners} refresh={refresh} />}
                    {tab === "clients" && <ClientsTab clients={clients} refresh={refresh} />}
                    {tab === "partners" && <PartnersTab partners={partners} refresh={refresh} />}
                    {tab === "inbox" && <InboxTab shootReqs={shootReqs} inquiries={inquiries} subscriptions={subscriptions} />}
                    {tab === "consents" && <ConsentsTab consents={consents} archive={archive} refresh={refresh} />}
                    {tab === "archive" && <ArchiveTab archive={archive} consents={consents} refresh={refresh} />}
                    {tab === "packages" && <PackagesTab packages={packages} archive={archive} clients={clients} refresh={refresh} />}
                    {tab === "invoices" && <InvoicesTab invoices={invoices} clients={clients} refresh={refresh} />}
                    {tab === "ai" && <AiTab />}
                </main>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        new: { c: "var(--gt-warn)", t: "New" },
        in_progress: { c: "var(--gt-accent)", t: "In progress" },
        delivered: { c: "var(--gt-text-2)", t: "Delivered" },
        invoiced: { c: "#9d6bff", t: "Invoiced" },
        paid: { c: "var(--gt-ok)", t: "Paid" },
        unpaid: { c: "var(--gt-warn)", t: "Unpaid" },
        sent: { c: "#9d6bff", t: "Sent" },
    };
    const m = map[status] || { c: "var(--gt-text-2)", t: status };
    return <span className="meta-chip" style={{ borderColor: m.c, color: m.c }}>{m.t}</span>;
}

function AssignmentsTab({ assignments, clients, partners, refresh }) {
    const [showNew, setShowNew] = useState(false);
    const [pitchFor, setPitchFor] = useState(null);
    const [pitchIds, setPitchIds] = useState([]);
    const [archiveList, setArchiveList] = useState([]);
    const [form, setForm] = useState({ title: "", client_id: "", brief: "", status: "new", deadline: "", deliverables: "", partner_id: "", partner_photographer: "", budget_usd: "" });

    useEffect(() => { if (pitchFor) api.get("/archive").then((r) => setArchiveList(r.data)); }, [pitchFor]);

    const togglePitch = (id) => setPitchIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : ids.length < 4 ? [...ids, id] : ids);
    const openPitch = (a) => { setPitchFor(a); setPitchIds([]); };
    const downloadPitch = () => {
        const q = pitchIds.length > 0 ? `?image_ids=${pitchIds.join(",")}` : "";
        const token = localStorage.getItem("gt_token");
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/assignments/${pitchFor.id}/pitch.pdf${q}${q ? "&" : "?"}auth=${encodeURIComponent(token)}`;
        window.open(url, "_blank");
        setPitchFor(null);
    };

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/assignments", { ...form, budget_usd: form.budget_usd ? Number(form.budget_usd) : null });
            toast.success("Assignment created");
            setShowNew(false);
            setForm({ title: "", client_id: "", brief: "", status: "new", deadline: "", deliverables: "", partner_id: "", partner_photographer: "", budget_usd: "" });
            refresh();
        } catch { toast.error("Could not create"); }
    };

    const updateStatus = async (a, status) => {
        await api.patch(`/assignments/${a.id}`, { ...a, status });
        toast.success(`Marked ${status.replace("_", " ")}`);
        refresh();
    };

    const remove = async (id) => { await api.delete(`/assignments/${id}`); refresh(); };

    return (
        <section>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline mb-1">Assignment Tracker</div>
                    <h1 className="font-display text-3xl">Field assignments</h1>
                </div>
                <button onClick={() => setShowNew((v) => !v)} className="gt-btn-primary px-4 py-2 text-sm" data-testid="assign-new-btn">
                    <i className="fa-solid fa-plus mr-2" style={{ fontSize: 11 }}></i> New
                </button>
            </div>

            {showNew && (
                <form onSubmit={create} className="gt-card p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="assign-new-form">
                    <input required placeholder="Title" className="gt-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <select required className="gt-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                        <option value="">Select client…</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.organization} — {c.name}</option>)}
                    </select>
                    <input type="date" className="gt-input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                    <input type="number" min="0" placeholder="Budget USD" className="gt-input" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: e.target.value })} />
                    <input placeholder="Partner photographer (free text)" className="gt-input" value={form.partner_photographer} onChange={(e) => setForm({ ...form, partner_photographer: e.target.value })} />
                    <select className="gt-input" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
                        <option value="">— or pick from roster —</option>
                        {partners.filter((p) => p.status !== "paused").map((p) => <option key={p.id} value={p.id}>{p.name} · {p.region}</option>)}
                    </select>
                    <input placeholder="Deliverables" className="gt-input" value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
                    <textarea required placeholder="Brief" className="gt-input md:col-span-2" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
                    <div className="md:col-span-2 flex justify-end">
                        <button className="gt-btn-primary px-5 py-2 text-sm">Create assignment</button>
                    </div>
                </form>
            )}

            <div className="space-y-3">
                {assignments.map((a) => (
                    <div key={a.id} className="gt-card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid={`assign-${a.id}`}>
                        <div className="lg:col-span-5">
                            <div className="overline" style={{ fontSize: 10 }}>{a.client?.organization || "—"} · {a.client?.name}</div>
                            <div className="font-display text-xl mt-1">{a.title}</div>
                            <p className="text-sm mt-2" style={{ color: "var(--gt-text-2)" }}>{a.brief}</p>
                        </div>
                        <div className="lg:col-span-4 text-sm space-y-1" style={{ color: "var(--gt-text-2)" }}>
                            <div>Deadline: <span className="text-white">{a.deadline || "—"}</span></div>
                            <div>Budget: <span className="text-white">${a.budget_usd || "—"}</span></div>
                            <div>Partner: <span className="text-white">{a.partner_photographer || "—"}</span></div>
                            <div>Deliverables: <span className="text-white">{a.deliverables || "—"}</span></div>
                        </div>
                        <div className="lg:col-span-3 space-y-2">
                            <StatusBadge status={a.status} />
                            <select className="gt-input mt-2" value={a.status} onChange={(e) => updateStatus(a, e.target.value)}>
                                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                            </select>
                            <button onClick={() => openPitch(a)} className="gt-btn-primary px-3 py-2 text-xs w-full" data-testid={`assign-pitch-${a.id}`}>
                                <i className="fa-solid fa-file-pdf mr-1"></i> Pitch Deck
                            </button>
                            <button onClick={() => remove(a.id)} className="gt-btn-ghost px-3 py-2 text-xs w-full">Delete</button>
                        </div>
                    </div>
                ))}
                {assignments.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No assignments yet. Create your first one above.</div>}
            </div>

            {pitchFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: "rgba(10,11,14,0.8)", backdropFilter: "blur(6px)" }} data-testid="pitch-modal">
                    <div className="gt-card p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto">
                        <div className="overline mb-2">Pitch Deck · 3 slides</div>
                        <h3 className="font-display text-2xl mb-1">{pitchFor.title}</h3>
                        <p className="text-sm mb-5" style={{ color: "var(--gt-text-2)" }}>Pick up to 4 teaser frames. First selection becomes the cover.</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
                            {archiveList.map((im) => {
                                const idx = pitchIds.indexOf(im.id);
                                return (
                                    <button key={im.id} type="button" onClick={() => togglePitch(im.id)} className="relative gt-card overflow-hidden text-left"
                                        style={{ border: `2px solid ${idx >= 0 ? "var(--gt-accent)" : "var(--gt-border)"}` }}>
                                        <img src={im.image_url || `${process.env.REACT_APP_BACKEND_URL}/api/archive/image/${im.id}`} alt="" className="w-full h-24 object-cover" />
                                        <div className="p-2 text-xs truncate">{im.title}</div>
                                        {idx >= 0 && (
                                            <span className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center font-mono text-xs" style={{ background: "var(--gt-accent)", color: "#1a0f0a" }}>{idx + 1}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="overline" style={{ fontSize: 10 }}>{pitchIds.length}/4 picked · fallback to 4 most recent if none</div>
                            <div className="flex gap-2">
                                <button onClick={() => setPitchFor(null)} className="gt-btn-ghost px-4 py-2 text-sm">Cancel</button>
                                <button onClick={downloadPitch} className="gt-btn-primary px-5 py-2.5 text-sm" data-testid="pitch-download">
                                    <i className="fa-solid fa-download mr-2" style={{ fontSize: 11 }}></i> Download Pitch PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function ClientsTab({ clients, refresh }) {
    const [form, setForm] = useState({ name: "", organization: "", email: "", region: "Geneva", notes: "", ngo_size: "mid" });
    const create = async (e) => {
        e.preventDefault();
        try { await api.post("/clients", form); toast.success("Client added"); setForm({ name: "", organization: "", email: "", region: "Geneva", notes: "", ngo_size: "mid" }); refresh(); }
        catch { toast.error("Could not add"); }
    };
    const remove = async (id) => { await api.delete(`/clients/${id}`); refresh(); };
    const copyPortal = (c) => {
        const url = `${window.location.origin}/portal/${c.portal_token}`;
        navigator.clipboard?.writeText(url).catch(() => {});
        toast.success("Portal link copied");
    };
    const emailPortal = async (c) => {
        try { await api.post(`/clients/${c.id}/send-portal-email`); toast.success(`Portal link emailed to ${c.email}`); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    };
    return (
        <section>
            <div className="overline mb-1">Client Manager</div>
            <h1 className="font-display text-3xl mb-6">Clients · International NGOs</h1>
            <form onSubmit={create} className="gt-card p-5 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="client-new-form">
                <input required placeholder="Contact name" className="gt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required placeholder="Organization" className="gt-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
                <input required type="email" placeholder="Email" className="gt-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <select className="gt-input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
                    {["Geneva", "London", "Washington DC", "Nairobi", "Delhi", "Guatemala City", "Other"].map((r) => <option key={r}>{r}</option>)}
                </select>
                <select className="gt-input" value={form.ngo_size} onChange={(e) => setForm({ ...form, ngo_size: e.target.value })}>
                    <option value="grassroots">Grassroots NGO</option>
                    <option value="mid">Mid-size NGO</option>
                    <option value="large">Large / Global NGO</option>
                </select>
                <button className="gt-btn-primary px-4 py-2 text-sm">Add client</button>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((c) => (
                    <div key={c.id} className="gt-card p-5" data-testid={`client-${c.id}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="overline" style={{ fontSize: 10 }}>{c.region} · {c.ngo_size || "mid"}</div>
                        </div>
                        <div className="font-display text-lg">{c.organization}</div>
                        <div className="text-sm" style={{ color: "var(--gt-text-2)" }}>{c.name}</div>
                        <a href={`mailto:${c.email}`} className="gt-link text-xs mt-1 inline-block">{c.email}</a>
                        {c.notes && <p className="text-xs mt-3" style={{ color: "var(--gt-text-2)" }}>{c.notes}</p>}
                        {c.portal_token && (
                            <div className="flex gap-2 mt-3 flex-wrap">
                                <button onClick={() => copyPortal(c)} className="gt-btn-ghost px-3 py-1.5 text-xs" data-testid={`client-portal-copy-${c.id}`}>
                                    <i className="fa-solid fa-link mr-1"></i> Copy portal link
                                </button>
                                <button onClick={() => emailPortal(c)} className="gt-btn-primary px-3 py-1.5 text-xs" data-testid={`client-portal-email-${c.id}`}>
                                    <i className="fa-solid fa-paper-plane mr-1"></i> Email portal
                                </button>
                            </div>
                        )}
                        {c.portal_emailed_at && <div className="overline mt-2" style={{ fontSize: 9 }}>Portal emailed {new Date(c.portal_emailed_at).toLocaleDateString()}</div>}
                        <button onClick={() => remove(c.id)} className="gt-btn-ghost mt-3 px-3 py-1.5 text-xs">Remove</button>
                    </div>
                ))}
                {clients.length === 0 && <div className="col-span-full gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No clients yet.</div>}
            </div>
        </section>
    );
}

function PartnersTab({ partners, refresh }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", region: "", specialties: "", status: "invited", notes: "" });
    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/partners", {
                ...form,
                specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Partner added to roster");
            setForm({ name: "", email: "", phone: "", region: "", specialties: "", status: "invited", notes: "" });
            refresh();
        } catch { toast.error("Could not add"); }
    };
    const remove = async (id) => { await api.delete(`/partners/${id}`); refresh(); };
    const setStatus = async (p, status) => { await api.patch(`/partners/${p.id}`, { ...p, status }); refresh(); };
    const invite = async (p) => {
        try { await api.post(`/partners/${p.id}/send-invite`); toast.success("Invite emailed"); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    };
    return (
        <section>
            <div className="overline mb-1">Partner Photographer Roster · Invite-only</div>
            <h1 className="font-display text-3xl mb-6">Vetted locals for on-demand assignments</h1>
            <form onSubmit={create} className="gt-card p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="partner-new-form">
                <input required placeholder="Name" className="gt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required type="email" placeholder="Email" className="gt-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input placeholder="Phone" className="gt-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input required placeholder="Region (e.g., Petén / Pacific Coast)" className="gt-input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
                <input placeholder="Specialties (comma-separated)" className="gt-input md:col-span-2" value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} />
                <textarea placeholder="Notes" className="gt-input md:col-span-3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <div className="md:col-span-3 flex justify-end">
                    <button className="gt-btn-primary px-5 py-2 text-sm">Invite to roster</button>
                </div>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.map((p) => (
                    <div key={p.id} className="gt-card p-5" data-testid={`partner-${p.id}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="overline" style={{ fontSize: 10 }}>{p.region}</div>
                            <StatusBadge status={p.status} />
                        </div>
                        <div className="font-display text-lg">{p.name}</div>
                        <a href={`mailto:${p.email}`} className="gt-link text-xs">{p.email}</a>
                        {p.phone && <div className="text-xs mt-1" style={{ color: "var(--gt-text-2)" }}>{p.phone}</div>}
                        {p.specialties?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                                {p.specialties.map((s) => <span key={s} className="meta-chip" style={{ fontSize: 9 }}>{s}</span>)}
                            </div>
                        )}
                        {p.notes && <p className="text-xs mt-3" style={{ color: "var(--gt-text-2)" }}>{p.notes}</p>}
                        <div className="flex gap-2 mt-4 flex-wrap">
                            <button onClick={() => invite(p)} className="gt-btn-primary px-3 py-1.5 text-xs" data-testid={`partner-invite-${p.id}`}>
                                <i className="fa-solid fa-paper-plane mr-1"></i> Email invite
                            </button>
                            <select value={p.status} onChange={(e) => setStatus(p, e.target.value)} className="gt-input px-2 py-1 text-xs" style={{ minHeight: 0 }}>
                                <option value="invited">Invited</option>
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                            </select>
                            <button onClick={() => remove(p.id)} className="gt-btn-ghost px-3 py-1.5 text-xs">Remove</button>
                        </div>
                        {p.invite_sent_at && <div className="overline mt-2" style={{ fontSize: 9 }}>Invited {new Date(p.invite_sent_at).toLocaleDateString()}</div>}
                    </div>
                ))}
                {partners.length === 0 && <div className="col-span-full gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No partners yet.</div>}
            </div>
        </section>
    );
}

function InboxTab({ shootReqs, inquiries, subscriptions }) {
    const [k, setK] = useState("shoots");
    return (
        <section>
            <div className="overline mb-1">Inbound Requests</div>
            <h1 className="font-display text-3xl mb-6">Inbox</h1>
            <div className="flex gap-2 mb-6">
                {[
                    { id: "shoots", l: `Shoot Requests (${shootReqs.length})` },
                    { id: "inquiries", l: `Inquiries (${inquiries.length})` },
                    { id: "subs", l: `Subscriptions (${subscriptions.length})` },
                ].map((o) => (
                    <button key={o.id} onClick={() => setK(o.id)} className="px-4 py-2 text-xs"
                        style={{ background: k === o.id ? "var(--gt-overlay)" : "transparent", border: "1px solid var(--gt-border)", color: k === o.id ? "var(--gt-text)" : "var(--gt-text-2)" }}>
                        {o.l}
                    </button>
                ))}
            </div>
            <div className="space-y-3">
                {k === "shoots" && shootReqs.map((r) => (
                    <div key={r.id} className="gt-card p-5" data-testid={`inbox-shoot-${r.id}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-display text-lg">{r.organization_name}</div>
                            <div className="overline" style={{ fontSize: 10 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="text-sm mb-2" style={{ color: "var(--gt-text-2)" }}>{r.contact_name} · <a href={`mailto:${r.contact_email}`} className="gt-link">{r.contact_email}</a></div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs mb-3">
                            <div className="meta-chip">{r.location_region}</div>
                            <div className="meta-chip">Deadline: {r.deadline || "—"}</div>
                            <div className="meta-chip">${r.budget_usd || "—"}</div>
                            <div className="meta-chip">{r.usage_rights}</div>
                        </div>
                        <p className="text-sm" style={{ color: "var(--gt-text)" }}>{r.brief_description}</p>
                    </div>
                ))}
                {k === "inquiries" && inquiries.map((r) => (
                    <div key={r.id} className="gt-card p-5">
                        <div className="flex items-center justify-between">
                            <div><div className="overline" style={{ fontSize: 10 }}>{r.kind}</div><div className="font-display text-lg">{r.name}</div></div>
                            <div className="overline" style={{ fontSize: 10 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="text-sm" style={{ color: "var(--gt-text-2)" }}>{r.organization} · <a className="gt-link" href={`mailto:${r.email}`}>{r.email}</a></div>
                        <p className="text-sm mt-2">{r.message}</p>
                    </div>
                ))}
                {k === "subs" && subscriptions.map((r) => (
                    <div key={r.id} className="gt-card p-5">
                        <div className="font-display text-lg">{r.organization_name}</div>
                        <div className="text-sm" style={{ color: "var(--gt-text-2)" }}><a href={`mailto:${r.contact_email}`} className="gt-link">{r.contact_email}</a> · {r.tier} · {r.cadence}</div>
                        {r.notes && <p className="text-sm mt-2">{r.notes}</p>}
                    </div>
                ))}
                {((k === "shoots" && shootReqs.length === 0) || (k === "inquiries" && inquiries.length === 0) || (k === "subs" && subscriptions.length === 0)) && (
                    <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>Inbox empty.</div>
                )}
            </div>
        </section>
    );
}

function ConsentsTab({ consents, archive, refresh }) {
    const [form, setForm] = useState({ subject_name: "", location: "", date_signed: "", usage_permissions: "editorial", notes: "", image_id: "" });
    const create = async (e) => {
        e.preventDefault();
        try { await api.post("/consents", form); toast.success("Consent recorded"); setForm({ subject_name: "", location: "", date_signed: "", usage_permissions: "editorial", notes: "", image_id: "" }); refresh(); }
        catch { toast.error("Could not record"); }
    };
    const remove = async (id) => { await api.delete(`/consents/${id}`); refresh(); };
    return (
        <section>
            <div className="overline mb-1">Consent & Rights Ledger</div>
            <h1 className="font-display text-3xl mb-6">Signed consents on record</h1>
            <form onSubmit={create} className="gt-card p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="consent-new-form">
                <input required placeholder="Subject name" className="gt-input" value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} />
                <input required placeholder="Location" className="gt-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                <input required type="date" className="gt-input" value={form.date_signed} onChange={(e) => setForm({ ...form, date_signed: e.target.value })} />
                <select className="gt-input" value={form.usage_permissions} onChange={(e) => setForm({ ...form, usage_permissions: e.target.value })}>
                    <option value="editorial">Editorial only</option>
                    <option value="commercial">Commercial</option>
                    <option value="ngo_advocacy">NGO Advocacy</option>
                    <option value="editorial, ngo_advocacy">Editorial + NGO</option>
                    <option value="all">All uses</option>
                </select>
                <select className="gt-input" value={form.image_id} onChange={(e) => setForm({ ...form, image_id: e.target.value })}>
                    <option value="">(Optional) Attach to image…</option>
                    {archive.map((im) => <option key={im.id} value={im.id}>{im.title}</option>)}
                </select>
                <button className="gt-btn-primary px-4 py-2 text-sm">Record consent</button>
                <textarea placeholder="Notes" className="gt-input md:col-span-3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </form>
            <div className="space-y-3">
                {consents.map((c) => (
                    <div key={c.id} className="gt-card p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                        <div className="md:col-span-4">
                            <div className="font-display text-base">{c.subject_name}</div>
                            <div className="overline" style={{ fontSize: 10 }}>{c.location}</div>
                        </div>
                        <div className="md:col-span-3 text-sm" style={{ color: "var(--gt-text-2)" }}>Signed {c.date_signed}</div>
                        <div className="md:col-span-3 meta-chip w-max">{c.usage_permissions}</div>
                        <button onClick={() => remove(c.id)} className="md:col-span-2 gt-btn-ghost px-3 py-1.5 text-xs">Remove</button>
                        {c.notes && <p className="md:col-span-12 text-sm" style={{ color: "var(--gt-text-2)" }}>{c.notes}</p>}
                    </div>
                ))}
                {consents.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No consents recorded.</div>}
            </div>
        </section>
    );
}

function PackagesTab({ packages, archive, clients, refresh }) {
    const [form, setForm] = useState({ name: "", client_id: "", client_email: "", image_ids: [], hero_image_id: "", notes: "", expires_in_days: 14 });
    const toggleImage = (id) => setForm((f) => ({
        ...f,
        image_ids: f.image_ids.includes(id) ? f.image_ids.filter((x) => x !== id) : [...f.image_ids, id],
        hero_image_id: f.hero_image_id === id && f.image_ids.includes(id) ? "" : f.hero_image_id,
    }));
    const create = async (e) => {
        e.preventDefault();
        if (form.image_ids.length === 0) return toast.error("Pick at least one image");
        try {
            const payload = { ...form, expires_in_days: Number(form.expires_in_days), hero_image_id: form.hero_image_id || null };
            const r = await api.post("/packages", payload);
            toast.success("Package created");
            setForm({ name: "", client_id: "", client_email: "", image_ids: [], hero_image_id: "", notes: "", expires_in_days: 14 });
            refresh();
            copyLink(r.data.download_token);
        } catch { toast.error("Could not create"); }
    };
    const copyLink = (token) => {
        const url = `${window.location.origin}/pkg/${token}`;
        navigator.clipboard?.writeText(url).catch(() => {});
        toast.success("Download link copied to clipboard");
    };
    const sendEmail = async (p) => {
        try { await api.post(`/packages/${p.id}/send-email`); toast.success(`Package link emailed to ${p.client_email}`); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    };
    const remove = async (id) => { await api.delete(`/packages/${id}`); refresh(); };
    return (
        <section>
            <div className="overline mb-1">Package Dispatcher</div>
            <h1 className="font-display text-3xl mb-6">Deliver story packages securely</h1>
            <form onSubmit={create} className="gt-card p-5 mb-6" data-testid="package-new-form">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                    <input required placeholder="Package name" className="gt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <select className="gt-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                        <option value="">Client (optional)</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.organization}</option>)}
                    </select>
                    <input type="email" placeholder="Client email" className="gt-input" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
                    <input type="number" min="1" placeholder="Expires (days)" className="gt-input" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} />
                </div>
                <div className="overline mb-2">Select images ({form.image_ids.length}) · click the star to set hero cover</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4 max-h-72 overflow-y-auto">
                    {archive.map((im) => {
                        const picked = form.image_ids.includes(im.id);
                        const isHero = form.hero_image_id === im.id;
                        return (
                            <div key={im.id} className="relative gt-card overflow-hidden"
                                style={{ border: `2px solid ${isHero ? "var(--gt-warn)" : picked ? "var(--gt-accent)" : "var(--gt-border)"}` }}>
                                <button type="button" onClick={() => toggleImage(im.id)} className="w-full text-left">
                                    <img src={im.image_url || `${process.env.REACT_APP_BACKEND_URL}/api/archive/image/${im.id}`} alt={im.title} className="w-full h-24 object-cover" />
                                    <div className="p-2 text-xs" style={{ color: "var(--gt-text-2)" }}>{im.title}</div>
                                </button>
                                {picked && (
                                    <button type="button" onClick={() => setForm((f) => ({ ...f, hero_image_id: isHero ? "" : im.id }))} className="absolute top-1 left-1 w-6 h-6 flex items-center justify-center" title={isHero ? "Hero cover" : "Set as hero cover"}
                                        style={{ background: isHero ? "var(--gt-warn)" : "rgba(10,11,14,0.7)" }} data-testid={`pkg-hero-${im.id}`}>
                                        <i className="fa-solid fa-star" style={{ fontSize: 9, color: isHero ? "#1a0f0a" : "var(--gt-warn)" }}></i>
                                    </button>
                                )}
                                {picked && (
                                    <span className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center" style={{ background: "var(--gt-accent)" }}>
                                        <i className="fa-solid fa-check" style={{ fontSize: 9, color: "#1a0f0a" }}></i>
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
                <textarea placeholder="Delivery notes" className="gt-input mb-3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <div className="flex justify-end">
                    <button className="gt-btn-primary px-5 py-2 text-sm">Create & copy link</button>
                </div>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {packages.map((p) => (
                    <div key={p.id} className="gt-card p-5">
                        <div className="font-display text-lg">{p.name}</div>
                        <div className="overline mt-1" style={{ fontSize: 10 }}>{p.image_ids.length} images · expires {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"}</div>
                        {p.client_email && <div className="text-xs mt-1" style={{ color: "var(--gt-text-2)" }}>→ {p.client_email}</div>}
                        <div className="flex gap-2 mt-4 flex-wrap">
                            <button onClick={() => copyLink(p.download_token)} className="gt-btn-primary px-3 py-2 text-xs" data-testid={`pkg-copy-${p.id}`}>
                                <i className="fa-solid fa-link mr-1"></i> Copy link
                            </button>
                            {p.client_email && (
                                <button onClick={() => sendEmail(p)} className="gt-btn-ghost px-3 py-2 text-xs" data-testid={`pkg-email-${p.id}`}>
                                    <i className="fa-solid fa-paper-plane mr-1"></i> Email client
                                </button>
                            )}
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/packages/download/${p.download_token}/dossier.pdf`} target="_blank" rel="noreferrer" className="gt-btn-ghost px-3 py-2 text-xs" data-testid={`pkg-dossier-${p.id}`}>
                                <i className="fa-solid fa-file-pdf mr-1"></i> Dossier PDF
                            </a>
                            <a href={`/pkg/${p.download_token}`} target="_blank" rel="noreferrer" className="gt-btn-ghost px-3 py-2 text-xs">Preview</a>
                            <button onClick={() => remove(p.id)} className="gt-btn-ghost px-3 py-2 text-xs">Delete</button>
                        </div>
                        {p.last_emailed_at && <div className="overline mt-2" style={{ fontSize: 10 }}>Last emailed {new Date(p.last_emailed_at).toLocaleString()}</div>}
                    </div>
                ))}
                {packages.length === 0 && <div className="col-span-full gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No packages yet.</div>}
            </div>
        </section>
    );
}

function InvoicesTab({ invoices, clients, refresh }) {
    const [form, setForm] = useState({ client_id: "", description: "", amount_usd: "" });
    const [busy, setBusy] = useState(null);
    const create = async (e) => {
        e.preventDefault();
        try { await api.post("/invoices", { ...form, amount_usd: Number(form.amount_usd) }); toast.success("Invoice created"); setForm({ client_id: "", description: "", amount_usd: "" }); refresh(); }
        catch { toast.error("Could not create"); }
    };
    const genLink = async (inv) => {
        setBusy(inv.id);
        try {
            const { data } = await api.post("/payments/checkout", { invoice_id: inv.id, origin_url: window.location.origin });
            navigator.clipboard?.writeText(data.checkout_url).catch(() => {});
            toast.success("Stripe link generated & copied");
            refresh();
        } catch (e) { toast.error("Could not generate link"); } finally { setBusy(null); }
    };
    const emailInvoice = async (inv) => {
        setBusy(inv.id);
        try {
            const { data } = await api.post(`/invoices/${inv.id}/send-email`);
            toast.success("Invoice emailed to client");
            refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); } finally { setBusy(null); }
    };
    const remove = async (id) => { await api.delete(`/invoices/${id}`); refresh(); };
    return (
        <section>
            <div className="overline mb-1">Invoice Generator</div>
            <h1 className="font-display text-3xl mb-6">USD invoices via Stripe</h1>
            <form onSubmit={create} className="gt-card p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="invoice-new-form">
                <select required className="gt-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                    <option value="">Select client…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.organization}</option>)}
                </select>
                <input required placeholder="Description (e.g., On-Demand Shoot — Sololá)" className="gt-input md:col-span-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <input required type="number" min="1" placeholder="Amount USD" className="gt-input" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })} />
                <div className="md:col-span-4 flex justify-end">
                    <button className="gt-btn-primary px-5 py-2 text-sm">Create invoice</button>
                </div>
            </form>
            <div className="space-y-3">
                {invoices.map((inv) => (
                    <div key={inv.id} className="gt-card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center" data-testid={`invoice-${inv.id}`}>
                        <div className="lg:col-span-5">
                            <div className="overline" style={{ fontSize: 10 }}>{inv.client?.organization || "—"}</div>
                            <div className="font-display text-lg mt-1">{inv.description}</div>
                        </div>
                        <div className="lg:col-span-2 font-mono text-2xl" style={{ color: "var(--gt-accent)" }}>${inv.amount_usd}</div>
                        <div className="lg:col-span-2"><StatusBadge status={inv.status} /></div>
                        <div className="lg:col-span-3 flex gap-2 flex-wrap">
                            {inv.checkout_url ? (
                                <a href={inv.checkout_url} target="_blank" rel="noreferrer" className="gt-btn-ghost px-3 py-2 text-xs">Open link</a>
                            ) : (
                                <button onClick={() => genLink(inv)} disabled={busy === inv.id} className="gt-btn-primary px-3 py-2 text-xs">
                                    {busy === inv.id ? "…" : <><i className="fa-solid fa-link mr-1"></i> Generate link</>}
                                </button>
                            )}
                            <button onClick={() => emailInvoice(inv)} disabled={busy === inv.id} className="gt-btn-primary px-3 py-2 text-xs" data-testid={`invoice-email-${inv.id}`}>
                                <i className="fa-solid fa-paper-plane mr-1"></i> Email
                            </button>
                            <button onClick={() => remove(inv.id)} className="gt-btn-ghost px-3 py-2 text-xs">Delete</button>
                        </div>
                        {inv.last_emailed_at && <div className="lg:col-span-12 overline" style={{ fontSize: 10 }}>Last emailed {new Date(inv.last_emailed_at).toLocaleString()}</div>}
                    </div>
                ))}
                {invoices.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No invoices yet.</div>}
            </div>
        </section>
    );
}

function ArchiveTab({ archive, consents, refresh }) {
    const [files, setFiles] = useState([]);
    const [form, setForm] = useState({ title: "", caption: "", region: "", theme: "Water & Climate", community: "", consent_id: "", c2pa_hash: "", gps: "", exif: "", date_shot: "" });
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = React.useRef(null);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    const onFiles = (f) => {
        const arr = Array.from(f || []).filter((x) => x.type.startsWith("image/"));
        setFiles(arr);
        if (arr[0] && !form.title) setForm((p) => ({ ...p, title: arr[0].name.replace(/\.[^.]+$/, "") }));
    };

    const upload = async (e) => {
        e.preventDefault();
        if (files.length === 0) return toast.error("Pick an image first");
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
                await api.post("/archive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(`Uploaded ${files.length} image${files.length === 1 ? "" : "s"}`);
            setFiles([]);
            setForm({ title: "", caption: "", region: "", theme: "Water & Climate", community: "", consent_id: "", c2pa_hash: "", gps: "", exif: "", date_shot: "" });
            if (inputRef.current) inputRef.current.value = "";
            refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
    };

    const remove = async (id) => { await api.delete(`/archive/${id}`); refresh(); };

    return (
        <section>
            <div className="overline mb-1">Archive Uploader</div>
            <h1 className="font-display text-3xl mb-6">Add field photos with consent & C2PA</h1>

            <form onSubmit={upload} className="gt-card p-6 mb-6" data-testid="archive-upload-form">
                <div
                    data-testid="archive-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
                    onClick={() => inputRef.current?.click()}
                    className="relative cursor-pointer flex flex-col items-center justify-center text-center py-10 mb-5"
                    style={{ border: `2px dashed ${dragOver ? "var(--gt-accent)" : "var(--gt-border-strong)"}`, background: dragOver ? "rgba(217,107,67,0.06)" : "var(--gt-bg)", transition: "all 180ms" }}
                >
                    <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 32, color: "var(--gt-accent)" }}></i>
                    <div className="mt-3 font-display text-lg">{files.length > 0 ? `${files.length} photo${files.length === 1 ? "" : "s"} ready` : "Drag & drop photos — or click to browse"}</div>
                    <div className="overline mt-2" style={{ fontSize: 10 }}>JPG · PNG · WebP · up to 25MB each</div>
                    <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid="archive-file-input" />
                </div>

                {files.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-5">
                        {files.map((f, i) => (
                            <div key={i} className="relative">
                                <img src={URL.createObjectURL(f)} alt={f.name} className="w-20 h-20 object-cover" style={{ border: "1px solid var(--gt-border)" }} />
                                <div className="overline mt-1 w-20 truncate" style={{ fontSize: 9 }}>{f.name}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input required placeholder="Title" className="gt-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="archive-title" />
                    <input required placeholder="Region (e.g., Guatemala Highlands)" className="gt-input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="archive-region" />
                    <select className="gt-input" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} data-testid="archive-theme">
                        {["Water & Climate", "Indigenous Rights", "Healthcare & Relief", "Migration & Borders", "Education", "Agriculture", "Livelihoods"].map((o) => <option key={o}>{o}</option>)}
                    </select>
                    <input placeholder="Community (e.g., Sololá)" className="gt-input" value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} />
                    <select className="gt-input" value={form.consent_id} onChange={(e) => setForm({ ...form, consent_id: e.target.value })} data-testid="archive-consent">
                        <option value="">Attach consent record (optional)…</option>
                        {consents.map((c) => <option key={c.id} value={c.id}>{c.subject_name} — {c.location}</option>)}
                    </select>
                    <input placeholder="GPS (e.g., 15.0284°N, 91.1502°W)" className="gt-input" value={form.gps} onChange={(e) => setForm({ ...form, gps: e.target.value })} />
                    <input placeholder="EXIF (e.g., 35mm · ISO 400 · 1/500s)" className="gt-input" value={form.exif} onChange={(e) => setForm({ ...form, exif: e.target.value })} />
                    <input type="date" className="gt-input" value={form.date_shot} onChange={(e) => setForm({ ...form, date_shot: e.target.value })} />
                </div>
                <textarea required placeholder="Caption — AP style" className="gt-input mb-3" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="archive-caption" />
                <div className="flex items-center justify-between">
                    <div className="overline" style={{ fontSize: 10 }}>C2PA hash auto-generated if blank · {files.length} selected</div>
                    <button disabled={uploading} className="gt-btn-primary px-6 py-3 text-sm" data-testid="archive-upload-btn">
                        {uploading ? "Uploading…" : <><i className="fa-solid fa-upload mr-2" style={{ fontSize: 11 }}></i> Upload to archive</>}
                    </button>
                </div>
            </form>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {archive.map((im) => {
                    const src = im.image_url || `${BACKEND_URL}/api/archive/image/${im.id}`;
                    return (
                        <div key={im.id} className="gt-card overflow-hidden" data-testid={`archive-mgr-${im.id}`}>
                            <img src={src} alt={im.title} className="w-full h-28 object-cover" />
                            <div className="p-2">
                                <div className="text-xs truncate" style={{ color: "var(--gt-text)" }}>{im.title}</div>
                                <div className="overline mt-1" style={{ fontSize: 9 }}>{im.region}</div>
                                <button onClick={() => remove(im.id)} className="mt-2 gt-btn-ghost w-full px-2 py-1 text-xs">Remove</button>
                            </div>
                        </div>
                    );
                })}
                {archive.length === 0 && <div className="col-span-full gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>Archive empty.</div>}
            </div>
        </section>
    );
}

function AiTab() {
    const [tool, setTool] = useState("caption");
    const [input, setInput] = useState("");
    const [audioFile, setAudioFile] = useState(null);
    const [output, setOutput] = useState("");
    const [loading, setLoading] = useState(false);

    const TOOLS = [
        { id: "caption", label: "Caption Draft (AP style)", icon: "fa-solid fa-align-left", path: "/ai/caption", key: "caption" },
        { id: "tags", label: "IPTC Keyword Tags", icon: "fa-solid fa-tags", path: "/ai/tags", key: "tags" },
        { id: "transcribe", label: "Transcript Cleanup (text)", icon: "fa-solid fa-align-justify", path: "/ai/transcribe", key: "transcript" },
        { id: "transcribe-audio", label: "Audio → Transcript (Whisper)", icon: "fa-solid fa-microphone-lines", path: "/ai/transcribe-audio", key: "transcript" },
        { id: "repurpose", label: "One Assignment → Multiple Outputs", icon: "fa-solid fa-shuffle", path: "/ai/repurpose", key: "outputs" },
    ];
    const current = TOOLS.find((t) => t.id === tool);
    const isAudio = tool === "transcribe-audio";

    const run = async () => {
        if (isAudio && !audioFile) return toast.error("Pick an audio file first");
        if (!isAudio && !input.trim()) return toast.error("Add some input first");
        setLoading(true); setOutput("");
        try {
            let data;
            if (isAudio) {
                const fd = new FormData(); fd.append("file", audioFile);
                const r = await api.post(current.path, fd, { headers: { "Content-Type": "multipart/form-data" } });
                data = r.data;
            } else {
                const body = tool === "repurpose"
                    ? { assignment_summary: input, audiences: ["donor_summary", "social_post", "newsletter_copy", "report_blurb"] }
                    : { subject: input };
                const r = await api.post(current.path, body);
                data = r.data;
            }
            const val = data[current.key];
            if (tool === "tags") setOutput(val.join(", "));
            else if (tool === "repurpose") setOutput(JSON.stringify(val, null, 2));
            else setOutput(val);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI call failed");
        } finally { setLoading(false); }
    };

    return (
        <section>
            <div className="overline mb-1">AI Field Assist · Claude Sonnet 5.5 & Whisper</div>
            <h1 className="font-display text-3xl mb-6">Turn one assignment into complete deliverables</h1>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-4 space-y-2">
                    {TOOLS.map((t) => (
                        <button key={t.id} onClick={() => { setTool(t.id); setOutput(""); setAudioFile(null); setInput(""); }} data-testid={`ai-tool-${t.id}`}
                            className="w-full text-left p-4 flex items-start gap-3 transition-colors"
                            style={{
                                background: tool === t.id ? "var(--gt-overlay)" : "var(--gt-card)",
                                border: `1px solid ${tool === t.id ? "var(--gt-accent)" : "var(--gt-border)"}`,
                            }}>
                            <i className={t.icon} style={{ color: "var(--gt-accent)", fontSize: 14, marginTop: 2 }}></i>
                            <div><div className="text-sm">{t.label}</div></div>
                        </button>
                    ))}
                </div>
                <div className="lg:col-span-8 space-y-4">
                    <div className="gt-card p-5">
                        <div className="overline mb-2">
                            {tool === "caption" && "Subject details · location, action, context"}
                            {tool === "tags" && "Caption or scene description to keyword"}
                            {tool === "transcribe" && "Raw interview notes to clean up"}
                            {tool === "transcribe-audio" && "Upload a recorded interview (mp3, m4a, wav, webm, up to 25MB)"}
                            {tool === "repurpose" && "Full assignment summary to repurpose"}
                        </div>
                        {isAudio ? (
                            <label className="flex flex-col items-center justify-center py-8 cursor-pointer" style={{ border: "2px dashed var(--gt-border-strong)" }} data-testid="ai-audio-drop">
                                <i className="fa-solid fa-microphone" style={{ color: "var(--gt-accent)", fontSize: 24 }}></i>
                                <div className="mt-3 text-sm">{audioFile ? audioFile.name : "Click to choose an audio file"}</div>
                                <input type="file" accept="audio/*,.mp3,.m4a,.wav,.webm" onChange={(e) => setAudioFile(e.target.files?.[0] || null)} className="hidden" data-testid="ai-audio-input" />
                            </label>
                        ) : (
                            <textarea className="gt-input" style={{ minHeight: 180 }} value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-input" placeholder="Paste or write field notes here…" />
                        )}
                        <div className="flex justify-end mt-3">
                            <button onClick={run} disabled={loading} className="gt-btn-primary px-5 py-2.5 text-sm" data-testid="ai-run-btn">
                                {loading ? "Running…" : <><i className="fa-solid fa-wand-magic-sparkles mr-2" style={{ fontSize: 11 }}></i> {isAudio ? "Transcribe with Whisper" : "Run with Claude"}</>}
                            </button>
                        </div>
                    </div>
                    {output && (
                        <div className="gt-card p-5" data-testid="ai-output">
                            <div className="flex items-center justify-between mb-3">
                                <div className="overline">Output</div>
                                <button onClick={() => { navigator.clipboard?.writeText(output); toast.success("Copied"); }} className="gt-btn-ghost px-3 py-1.5 text-xs">
                                    <i className="fa-solid fa-copy mr-1"></i> Copy
                                </button>
                            </div>
                            <pre className="whitespace-pre-wrap text-sm font-body" style={{ color: "var(--gt-text)" }}>{output}</pre>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
